/**
 * Display a date-only value without letting the timezone move it.
 *
 * Maria Elena, Aug 7: "DOB on user info is one day off but it is correct on the LOR."
 *
 * A date-only column comes back as "2004-10-04". `new Date("2004-10-04")` parses that as midnight
 * **UTC**, and `toLocaleDateString('en-US')` then renders it in the viewer's timezone — so in Texas, five
 * or six hours behind, it displays as 3 October. Every date-only field rendered that way is a day
 * early for anyone west of Greenwich.
 *
 * The letter of representation was never wrong because it is generated on the server, whose
 * container runs in UTC, so the shift is zero there. That is exactly why this looked like a display
 * quirk rather than a data problem: the data was always right.
 *
 * A birth date has no time and no timezone. It is three numbers, and the fix is to read them as
 * written instead of routing them through an instant in time.
 *
 * ## And the format is pinned to the US
 *
 * `toLocaleDateString()` with no locale renders in the **viewer's** locale. Raquel's screenshots on
 * 14 Sep showed `23/9/2026` and a date filter reading `dd/mm/aaaa`, because her browser is set to
 * Spanish. For a Texas firm that is worse than untidy: `3/12/2026` is 3 December on one screen and
 * 12 March on another, with nothing on the page saying which, and a court date is the one number
 * nobody can afford to misread.
 */

/** "2004-10-04" (or an ISO timestamp) → "10/4/2004", with no timezone shift. */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return 'Not provided';

  // Accepts a bare date or the date half of an ISO timestamp.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) {
    // Not a shape we recognise. Show it as-is rather than guessing at it.
    return value;
  }

  const [, year, month, day] = match;
  // Constructed from parts in LOCAL time, so the calendar day is the one that was stored.
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-US');
}
